import Analytics from '@opencare/analytics';

import Logger from 'utils/Logger';

interface ErrorResponse {
  response?: {
    status?: number;
    data?: {
      errors?: string[];
      message?: string;
      code?: string;
    };
  };
  message?: string;
}

export const isErrorResponse = (error: unknown): error is ErrorResponse => {
  return typeof error === 'object' && error !== null && 'response' in error;
};

const isError = (error: unknown): error is Error => {
  return error instanceof Error;
};

export const extractErrorMessage = (error: unknown) => {
  const apiMessage = isErrorResponse(error) ? error.response?.data?.errors?.[0] : null;
  const errorMessage = isError(error) ? error.message : null;

  // Failsafe this method just in case no message is found
  return apiMessage || errorMessage || 'Unknown error';
};

export const extractErrorDetails = (error: ErrorResponse) => {
  const apiMessage = error?.response?.data?.message;

  if (apiMessage) {
    return {
      error_details: apiMessage,
    };
  }

  return null;
};

export const extractErrorCode = (error: ErrorResponse): string | undefined => {
  return error?.response?.data?.code;
};

const analytics = new Analytics();

/**
 * Send an error to Rollbar.
 */
export const sendToLogger = (error: unknown, methodName: string, errorDetails?: string) => {
  const errorMessage = String(extractErrorMessage(error));
  const errorData = isErrorResponse(error) ? error.response?.data : null;

  // We should not send any potentially sensitive information to the analytics package.
  analytics.trackError({
    pageType: analytics.pageTypes.ERROR,
    pageName: 'sendToLogger',
    content: {
      method_that_threw_error: JSON.stringify(methodName),
    },
  });

  const errorToLog = typeof error === 'string' ? new Error(error) : error;

  // Replace all occurrences of emails with a censored form.
  // Use a fixed censored email so that these errors can be grouped.
  const sanitizedErrorMessage = errorMessage.replaceAll(/\b\w+@\w+\.\w+\b/g, '****@****.***');

  // Anything in customData is sent directly to Rollbar
  const customData = {
    error,
    error_data: errorData,
    error_message: sanitizedErrorMessage,
    method_that_threw_error: methodName,
    error_details: errorDetails,
  };

  // We can send any useful information to rollbar because we have a BAA with them.
  Logger.error(`API: ${sanitizedErrorMessage}`, errorToLog, customData);
};
